/**
 * Server state for build step 9 — Capture and the held list (packages/contracts routes.step9):
 *   POST /capture/notes, GET /capture/notes, GET /capture/notes/{id},
 *   PATCH /capture/notes/{id}/items/{iid}, POST …/pause, …/resume, …/confirm, …/cancel, …/undo,
 *   GET /held-items, GET /held-items/backlog, POST /held-items/{id}/confirm, POST /held-items/{id}/discard,
 *   GET /capture/auto-log, POST /capture/auto-log/{task_id}/undo, GET /me/capture-settings.
 *
 * No audio reaches our server (ADR-002 §24.5): a spoken note is dictated live through the browser's speech service
 * (lib/speech) and the member edits the text before saving (`text_origin: browser`, `transcript_source: live`, with
 * `audio_seconds`); typed or pasted notes are `typed`. Either goes to the configured language model. Every write refreshes the work lists, because a captured note makes real tasks.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  CaptureNote,
  CreateCaptureNoteBody,
  ConfirmHeldBody,
  DiscardHeldBody,
  RouteQuery,
  UndoAutoCreatedBody,
  UpdateCaptureItemBody,
} from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../api';
import { refreshAfterWrite, allPages } from './work';

export const capkeys = {
  all: ['capture'] as const,
  note: (id: string) => ['capture', 'note', id] as const,
  notes: ['capture', 'notes'] as const,
  held: ['capture', 'held'] as const,
  backlog: ['capture', 'backlog'] as const,
  autoLog: ['capture', 'auto-log'] as const,
  settings: ['capture', 'settings'] as const,
};

export function refreshCapture(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: capkeys.all });
  refreshAfterWrite(qc);
}

// ---------------------------------------------------------------- reads

/** Polls while the server is still working on the note (proposing) or the countdown runs. */
export function useCaptureNote(id: string | null) {
  return useQuery({
    queryKey: capkeys.note(id ?? ''),
    queryFn: () => call('capture.getNote', { params: { id: id! } }),
    enabled: !!id,
    refetchInterval: (q) => {
      const s = q.state.data?.state;
      if (!s) return 2000;
      if (s === 'proposing') return 2000;
      if (s === 'proposed') return 3000;
      return false;
    },
  });
}

export function useMyNotes() {
  return useQuery({ queryKey: capkeys.notes, queryFn: () => call('capture.listNotes', { query: { limit: 10 } as RouteQuery<'capture.listNotes'> }) });
}

export function useHeld(enabled = true) {
  return useQuery({
    queryKey: capkeys.held,
    queryFn: () => allPages((cursor) => call('held.list', { query: { status: 'open', limit: 200, cursor } as RouteQuery<'held.list'> })),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useHeldBacklog(enabled: boolean) {
  return useQuery({ queryKey: capkeys.backlog, queryFn: () => call('held.backlog'), enabled, refetchInterval: 60_000 });
}

export function useAutoLog(enabled = true, limit = 50) {
  return useQuery({
    queryKey: [...capkeys.autoLog, limit],
    queryFn: () => call('autoLog.list', { query: { limit } as RouteQuery<'autoLog.list'> }),
    enabled,
  });
}

export function useCaptureSettings() {
  return useQuery({ queryKey: capkeys.settings, queryFn: () => call('me.captureSettings') });
}

// ---------------------------------------------------------------- the personal capture link (/c/:link)

/** What the member sends: the text they checked, and how long they dictated it live (absent when typed). */
export interface NoteInput {
  text: string;
  live?: { seconds: number };
}

/** A note's text and where it came from: dictated live in the browser, or typed. */
export const noteText = (v: NoteInput): Pick<CreateCaptureNoteBody, 'text' | 'text_origin' | 'transcript_source' | 'audio_seconds'> =>
  v.live
    ? { text: v.text, text_origin: 'browser', transcript_source: 'live', audio_seconds: v.live.seconds }
    : { text: v.text, text_origin: 'typed' };

/** GET /capture/link?t= — the greeting and the recogniser's language; nothing else is readable with a token. */
export const captureLinkInfo = (t: string) => call('capture.linkInfo', { query: { t } });

/** POST /capture/notes?t= with source 'link' — create-only; the 202 is all the link can ever read back. */
export const createLinkNote = (t: string, v: NoteInput) =>
  call('capture.createNote', {
    query: { t },
    body: {
      source: 'link',
      ...noteText(v),
    },
    idempotencyKey: newIdempotencyKey(),
  });

// ---------------------------------------------------------------- writes

function useCapWrite<V, R>(fn: (v: V) => Promise<R>, after?: (qc: QueryClient, r: R, v: V) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r, v) => {
      after?.(qc, r, v);
      refreshCapture(qc);
    },
    onError: () => refreshCapture(qc),
  });
}

const putNote = (qc: QueryClient, n: CaptureNote) => qc.setQueryData(capkeys.note(n.note.id), n);

/**
 * POST /capture/notes. `live` is set when the member dictated the note through the browser's speech service: it is
 * sent as text_origin 'browser' + transcript_source 'live' with audio_seconds. No audio is ever sent.
 */
export const useCreateNote = () =>
  useCapWrite((v: NoteInput) =>
    call('capture.createNote', {
      body: {
        source: 'app',
        ...noteText(v),
      },
      idempotencyKey: newIdempotencyKey(),
    }),
  );

export const useUpdateItem = () =>
  useCapWrite(
    (v: { noteId: string; itemId: string; version: number; body: UpdateCaptureItemBody }) =>
      call('capture.updateItem', { params: { id: v.noteId, iid: v.itemId }, body: v.body, ifMatch: v.version }),
  );

export const usePause = () => useCapWrite((id: string) => call('capture.pause', { params: { id } }), (qc, n) => putNote(qc, n));
export const useResume = () => useCapWrite((id: string) => call('capture.resume', { params: { id } }), (qc, n) => putNote(qc, n));
export const useCancelNote = () => useCapWrite((id: string) => call('capture.cancel', { params: { id } }));
/** POST /users/{id}/capture-token/rotate — a new personal link (shown once); refreshes capture_link_active. */
export const useRotateCaptureLink = () => useCapWrite((userId: string) => call('users.rotateCaptureToken', { params: { id: userId } }));
export const useUndoNote = () => useCapWrite((id: string) => call('capture.undoNote', { params: { id } }));
export const useConfirmNote = () =>
  useCapWrite(
    (v: { id: string; item_ids: string[]; escalation_item_ids: string[] }) =>
      call('capture.confirm', {
        params: { id: v.id },
        body: { item_ids: v.item_ids, escalation_item_ids: v.escalation_item_ids },
        idempotencyKey: newIdempotencyKey(),
      }),
    (qc, r) => putNote(qc, r.note),
  );

export const useConfirmHeld = () =>
  useCapWrite((v: { id: string; body: ConfirmHeldBody }) =>
    call('held.confirm', { params: { id: v.id }, body: v.body, idempotencyKey: newIdempotencyKey() }),
  );
export const useDiscardHeld = () =>
  useCapWrite((v: { id: string; body: DiscardHeldBody }) =>
    call('held.discard', { params: { id: v.id }, body: v.body, idempotencyKey: newIdempotencyKey() }),
  );
export const useUndoAuto = () =>
  useCapWrite((v: { taskId: string; body: UndoAutoCreatedBody }) =>
    call('autoLog.undo', { params: { task_id: v.taskId }, body: v.body, idempotencyKey: newIdempotencyKey() }),
  );

// ---------------------------------------------------------------- helpers

/** Prototype `.conf` tone: h at/above the threshold, m from 60, l below. */
export const confTone = (c: number, threshold: number): 'h' | 'm' | 'l' => (c >= threshold ? 'h' : c >= 60 ? 'm' : 'l');

/** "01:45". */
export const mmss = (s: number): string => {
  const v = Math.max(0, Math.round(s));
  return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`;
};

/** "Just now", "12 min ago", "3h ago", "2d ago". */
export function ago(iso: string, now = Date.now()): string {
  const m = Math.max(0, Math.round((now - new Date(iso).getTime()) / 60000));
  if (m < 1) return 'Just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
